import { Box, Paper, Table, Text } from '@mantine/core';
import { MRT_ColumnDef, MRT_TableOptions } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GesStatusIcon } from '../ges-status-icon';
import { DetailedResultEntry, Lot } from '../../data-access/types/results';
import { useResultPage } from '../../hooks/use-result-page';
import {
  formatMetricCell,
  formatNumber,
  formatThresholdRange,
} from '../../utils/format-metrics';

import { StackedLabelsCell, StackedValuesCell } from './stacked-table-cells';

import { PfTable } from '@ui';

function formatLotPratiques(lot: Lot): string {
  const pairs = (lot.pratiques ?? []).map(
    (pratique) => `${pratique.pratique_pk} / ${pratique.oleoze_id ?? '—'}`,
  );

  return pairs.length > 0 ? pairs.join(' · ') : '-';
}

interface ResultLotsInlineTableProps {
  lots: Lot[];
  headers: string[];
}

const ResultLotsInlineTable: React.FC<ResultLotsInlineTableProps> = ({
  lots,
  headers,
}) => (
  <Box pb="xs" pt={0}>
    <Table
      horizontalSpacing="sm"
      verticalSpacing={6}
      withColumnBorders={false}
      withRowBorders={false}
    >
      <Table.Thead>
        <Table.Tr>
          {headers.map((header) => (
            <Table.Th key={header}>
              <Text c="dimmed" fw={500} size="xs">
                {header}
              </Text>
            </Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {lots.map((lot: Lot) => (
          <Table.Tr key={lot.id}>
            <Table.Td>
              <Text size="sm">{formatLotPratiques(lot)}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{lot.species?.name || '-'}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{lot.campaign?.year ?? '-'}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{`${formatNumber(lot.tonnage)} t`}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{`${formatNumber(lot.area)} ha`}</Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  </Box>
);

const ResultPointsTable: React.FC = () => {
  const { t } = useTranslation();
  const { result } = useResultPage();

  const data = result.data?.results ?? [];
  const lotHeaders = useMemo(
    () => [
      t('common.code'),
      t('common.species'),
      t('common.campaign'),
      t('common.tonnage'),
      t('common.area'),
    ],
    [t],
  );

  const columns = useMemo<MRT_ColumnDef<DetailedResultEntry>[]>(
    () => [
      {
        id: 'result_type',
        header: '',
        enableColumnActions: false,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <StackedLabelsCell
            labels={[
              `CSR ${row.original.csr_year_reference ?? '-'}`,
              `CSA ${row.original.csa_year_reference ?? '-'}`,
            ]}
          />
        ),
      },
      {
        id: 'soil_density',
        header: t('common.densite-du-sol'),
        Cell: ({ row }) => (
          <StackedValuesCell
            values={[
              formatMetricCell(row.original.csr_soil_density, 'g/cm³'),
              formatMetricCell(row.original.csa_soil_density, 'g/cm³'),
            ]}
          />
        ),
      },
      {
        id: 'carbon_stock',
        header: t('common.carbon_stock'),
        Cell: ({ row }) => (
          <StackedValuesCell
            values={[
              formatMetricCell(row.original.csr_carbon_stock, 'tC/ha'),
              formatMetricCell(row.original.csa_carbon_stock, 'tC/ha'),
            ]}
          />
        ),
      },
      {
        id: 'threshold_range',
        header: `${t('common.lower_carbon_stock_threshold')} - ${t('common.upper_carbon_stock_threshold')}`,
        Cell: ({ row }) => (
          <StackedValuesCell
            values={[
              formatThresholdRange(
                row.original.csr_lower_carbon_stock_threshold,
                row.original.csr_upper_carbon_stock_threshold,
              ),
              formatThresholdRange(
                row.original.csa_lower_carbon_stock_threshold,
                row.original.csa_upper_carbon_stock_threshold,
              ),
            ]}
          />
        ),
      },
      {
        accessorKey: 'ges_status',
        header: t('table.columns.status'),
        Cell: ({ cell }) => {
          const status = cell.getValue<number | string>();
          return <GesStatusIcon value={status} />;
        },
      },
    ],
    [t],
  );

  const tableOptions: MRT_TableOptions<DetailedResultEntry> = {
    columns,
    data,
    getRowId: (row) => String(row.id),
    enablePagination: false,
    enableExpanding: true,
    initialState: { expanded: true },
    mantineDetailPanelProps: {
      style: {
        backgroundColor: 'transparent',
        borderBottom: 'none',
        padding: 0,
      },
    },
    renderDetailPanel: ({ row }) => (
      <ResultLotsInlineTable headers={lotHeaders} lots={row.original.lots ?? []} />
    ),
    mantinePaperProps: { h: '100%' },
  };

  return (
    <Paper h="100%" p={0} withBorder>
      <PfTable<DetailedResultEntry> options={tableOptions} />
    </Paper>
  );
};

export default ResultPointsTable;
